import type { PayloadAction } from "@reduxjs/toolkit";
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { directSelectionRepository } from "@src/core/engine/directSelection/directSelectionRepository";
import { productRepository } from "@src/core/engine/products/productRepository";
import { QualifiedId } from "@src/core/engine/products/types";
import { RootState } from "@src/core/store";

type DirectSelectionState = {
  disabledProducts: QualifiedId[];
  selectedProducts: QualifiedId[];
};

const initialState: DirectSelectionState = {
  disabledProducts: [],
  selectedProducts: [],
};

type ProductAction = {
  add: boolean;
  id: string;
  type: string;
  workWithDeleted: boolean;
};

export const directSelectionSlice = createSlice({
  initialState,
  name: "directSelection",
  reducers: {
    resetSelection: (state) => {
      state.disabledProducts = [];
      state.selectedProducts = [];
    },
    toggleProduct: (state, action: PayloadAction<ProductAction>) => {
      const toggle = action.payload.add
        ? directSelectionRepository.select
        : directSelectionRepository.unselect;

      const selectionRules = toggle(
        { ...action.payload, productSetId: null },
        state.selectedProducts,
        action.payload.workWithDeleted,
      );
      state.selectedProducts = selectionRules.selectedIds;
      state.disabledProducts = selectionRules.disabledIds;
    },
  },
});

export const { resetSelection, toggleProduct } = directSelectionSlice.actions;

const disabledProductsSelector = (state: RootState) =>
  state.directSelection.disabledProducts;
const selectedProductsSelector = (state: RootState) =>
  state.directSelection.selectedProducts;

export const disabledProductIdsSelector = createSelector(
  [disabledProductsSelector],
  (products) => products.map(({ id }) => id),
);
export const selectedProductIdsSelector = createSelector(
  [selectedProductsSelector],
  (products) => products.map(({ id }) => id),
);

export const selectedProductsDetailsSelector = createSelector(
  [selectedProductsSelector],
  productRepository.get,
);
